import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DashboardPageHeader from "../../components/Dashboard/DashboardPageHeader";
import {
  getDateRanges,
  getShowingText,
  translateEsimStatus,
} from "../../i18n/dashboardHelpers";
import {
  getAdminEsimStats,
  getAdminEsims,
  getProviderEsimStats,
  getProviderEsims,
  syncProviderEsims,
} from "../../services/apiService";
import { isPartner } from "../../utils/auth";

const PAGE_SIZE = 20;

const adminTabs = [
  { id: "all", label: "All" },
  { id: "active", label: "Active", status: "in_service" },
  { id: "expired", label: "Expired", status: "terminated" },
  { id: "pending", label: "Pending", status: "pre_service" },
];

const partnerTabs = [
  { id: "all", label: "All" },
  { id: "active", label: "Active", status: "in_service" },
  { id: "expired", label: "Expired", status: "terminated" },
  { id: "pending", label: "Pending", status: "pre_service" },
];

const adminStatusStyles = {
  PENDING: "dashboard-badge--pending",
  COMPLETED: "dashboard-badge--completed",
  CANCELED: "dashboard-badge--canceled",
};

const partnerStatusStyles = {
  ACTIVE: "dashboard-badge--completed",
  EXPIRED: "dashboard-badge--canceled",
  PENDING: "dashboard-badge--pending",
};

const STAT_ICONS = {
  total: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="5" y="2" width="14" height="20" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M9 7h6M9 11h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  pending: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="6" cy="12" r="1.5" fill="currentColor" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
      <circle cx="18" cy="12" r="1.5" fill="currentColor" />
    </svg>
  ),
  active: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M20 6L9 17l-5-5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  expired: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M18 6L6 18M6 6l12 12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  ),
};

function getDateRange(days) {
  const end = new Date();
  end.setUTCHours(23, 59, 59, 999);

  const start = new Date();
  start.setUTCDate(start.getUTCDate() - days + 1);
  start.setUTCHours(0, 0, 0, 0);

  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
}

function extractItems(response) {
  if (Array.isArray(response?.data)) return response.data;
  return response?.data?.data ?? [];
}

function extractTotal(response, fallback = 0) {
  return (
    response?.pagination?.total ??
    response?.pagination?.totalItems ??
    response?.data?.total ??
    response?.data?.meta?.total ??
    fallback
  );
}

function extractTotalPages(response, totalItems = 0) {
  if (response?.pagination?.totalPages != null) {
    return response.pagination.totalPages;
  }
  return totalItems === 0 ? 0 : Math.ceil(totalItems / PAGE_SIZE);
}

function getPaginationPages(currentPage, totalPages) {
  if (totalPages <= 0) return [];

  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 3) {
    return [1, 2, 3, "ellipsis", totalPages];
  }

  if (currentPage >= totalPages - 2) {
    return [1, "ellipsis", totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, "ellipsis", currentPage - 1, currentPage, currentPage + 1, "ellipsis", totalPages];
}

function extractStatValue(stats, key) {
  const value = stats?.[key];
  if (value == null) return 0;
  if (typeof value === "object") return value.count ?? value.value ?? 0;
  return value;
}

function extractStatRate(stats, key) {
  return stats?.rate?.[key] ?? null;
}

const PARTNER_STAT_KEYS = {
  total: ["total"],
  active: ["active", "in_service"],
  expired: ["expired", "terminated"],
  pending: ["pending", "pre_service", "waiting_for_assignment"],
};

function extractPartnerStatValue(stats, key) {
  const keys = PARTNER_STAT_KEYS[key] ?? [key];

  for (const statKey of keys) {
    if (stats?.[statKey] != null) {
      return extractStatValue(stats, statKey);
    }
  }

  return 0;
}

function extractPartnerStatRate(stats, key) {
  const keys = PARTNER_STAT_KEYS[key] ?? [key];

  for (const statKey of keys) {
    const rate = extractStatRate(stats, statKey);
    if (rate != null) return rate;
  }

  return null;
}

function formatRate(rate) {
  if (rate == null || Number.isNaN(Number(rate))) {
    return null;
  }

  const numericRate = Number(rate);
  const sign = numericRate >= 0 ? "+" : "";
  return `${sign}${numericRate.toFixed(1)}%`;
}

function getProviderLabel(request) {
  return request.provider?.fullName || request.provider?.email || "—";
}

function formatDate(value, locale) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function getInitials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

function avatarColor(name = "") {
  const colors = ["#DBEAFE", "#D1FAE5", "#FEF3C7", "#FCE7F3", "#E5E7EB"];
  const index = name.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0) % colors.length;
  return colors[index];
}

function TrendIndicator({ change, period, direction }) {
  if (direction === "neutral") {
    return <span className="dashboard-trend dashboard-trend--neutral">{change}</span>;
  }

  const isPositive = direction === "up";
  return (
    <span className={`dashboard-trend ${isPositive ? "" : " dashboard-trend--down"}`}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        {isPositive ? (
          <path
            d="M7 14l4-4 3 3 5-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : (
          <path
            d="M7 10l4 4 3-3 5 6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </svg>
      {change} {period}
    </span>
  );
}

function TrendBadge({ value }) {
  if (!value) return null;

  const isPositive = !value.startsWith("-");
  return (
    <span className={`dashboard-trend${isPositive ? "" : " dashboard-trend--down"}`}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        {isPositive ? (
          <path
            d="M7 14l4-4 3 3 5-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : (
          <path
            d="M7 10l4 4 3-3 5 6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </svg>
      {value}
    </span>
  );
}

function AdminStatCard({ label, value, change, period, direction }) {
  return (
    <div className="dashboard-stat-card">
      <p className="dashboard-stat-card__label">{label}</p>
      <p className="dashboard-stat-card__value">{value}</p>
      <TrendIndicator change={change} period={period} direction={direction} />
    </div>
  );
}

function PartnerStatCard({ label, value, change, icon, iconClass }) {
  return (
    <div className="dashboard-stat-card dashboard-stat-card--with-icon">
      <p className="dashboard-stat-card__label">{label}</p>
      <div className="dashboard-stat-card__row">
        <div className="dashboard-stat-card__content">
          <p className="dashboard-stat-card__value">{value}</p>
          {change && <TrendBadge value={change} />}
        </div>
        <span className={`dashboard-stat-card__icon ${iconClass}`}>{icon}</span>
      </div>
    </div>
  );
}

function StatusBadge({ status, partnerView, t }) {
  const label = translateEsimStatus(t, status);
  const normalized = String(status || "").toLowerCase();
  const styles = partnerView ? partnerStatusStyles : adminStatusStyles;
  const styleKey = partnerView
    ? {
        in_service: "ACTIVE",
        terminated: "EXPIRED",
        pre_service: "PENDING",
        waiting_for_assignment: "PENDING",
      }[normalized]
    : {
        pending: "PENDING",
        approved: "COMPLETED",
        rejected: "CANCELED",
      }[normalized];
  return <span className={`dashboard-badge ${styles[styleKey] || ""}`}>{label}</span>;
}

function SyncConfirmModal({ isOpen, onClose, onConfirm, t }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setIsSubmitting(false);
      setError("");
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape" && !isSubmitting) onClose();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, isSubmitting, onClose]);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    setError("");
    setIsSubmitting(true);

    try {
      await syncProviderEsims();
      onConfirm?.();
      onClose();
    } catch (fetchError) {
      setError(
        fetchError?.response?.data?.message ||
          fetchError?.message ||
          t("dashboard.esims.syncFailed")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="settings-modal-overlay"
      onClick={isSubmitting ? undefined : onClose}
      role="presentation"
    >
      <div
        className="settings-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="esim-sync-confirm-title"
      >
        <div className="settings-modal__header">
          <h2 id="esim-sync-confirm-title" className="settings-modal__title">
            {t("dashboard.esims.syncTitle")}
          </h2>
          <button
            type="button"
            className="settings-modal__close"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label={t("dashboard.common.close")}
          >
            &times;
          </button>
        </div>

        <p className="settings-modal__subtitle">
          {t("dashboard.esims.syncMessage")}
        </p>

        {error ? (
          <p className="settings-form-status settings-form-status--error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="settings-modal__actions">
          <button
            type="button"
            className="settings-btn settings-btn--outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {t("dashboard.common.cancel")}
          </button>
          <button
            type="button"
            className="settings-btn settings-btn--primary"
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? t("dashboard.common.syncing") : t("dashboard.common.sync")}
          </button>
        </div>
      </div>
    </div>
  );
}

function ActivationCodeCell({ code, t }) {
  const [copied, setCopied] = useState(false);

  if (!code) return "—";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="dashboard-table__activation-code">
      <span className="dashboard-table__activation-code-text" title={code}>
        {code}
      </span>
      <button
        type="button"
        className="dashboard-table__copy-btn"
        onClick={handleCopy}
        aria-label={copied ? t("dashboard.common.copied") : t("dashboard.common.copy")}
      >
        {copied ? t("dashboard.common.copied") : t("dashboard.common.copy")}
      </button>
    </div>
  );
}

function Esims() {
  const { t, i18n } = useTranslation();
  const partnerView = isPartner();
  const dateRanges = useMemo(() => getDateRanges(t), [t]);
  const tabs = partnerView ? partnerTabs : adminTabs;

  const [activeTab, setActiveTab] = useState("all");
  const [dateRange, setDateRange] = useState("7");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState("");
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);

  const selectedRange = dateRanges.find((range) => range.value === dateRange) ?? dateRanges[0];
  const { startDate, endDate } = useMemo(
    () => getDateRange(selectedRange.days),
    [selectedRange.days]
  );

  const activeStatus = tabs.find((tab) => tab.id === activeTab)?.status;

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [search]);

  const fetchPartnerStats = useCallback(async () => {
    setStatsLoading(true);

    try {
      const response = await getProviderEsimStats({ startDate, endDate });
      setStats(response?.data ?? response ?? null);
    } catch {
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, [startDate, endDate]);

  const fetchAdminStats = useCallback(async () => {
    setStatsLoading(true);

    try {
      const response = await getAdminEsimStats({ startDate, endDate });
      setStats(response?.data ?? response ?? null);
    } catch {
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, [startDate, endDate]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      if (partnerView) {
        const response = await getProviderEsims({
          status: activeStatus,
          search: debouncedSearch || undefined,
          startDate,
          endDate,
          page,
          pageSize: PAGE_SIZE,
        });
        const nextItems = extractItems(response);
        const total = extractTotal(response, nextItems.length);
        setItems(nextItems);
        setTotalItems(total);
        setTotalPages(extractTotalPages(response, total));
      } else {
        const response = await getAdminEsims({
          status: activeStatus,
          search: debouncedSearch || undefined,
          startDate,
          endDate,
          page,
          pageSize: PAGE_SIZE,
        });
        const nextItems = extractItems(response);
        const total = extractTotal(response, nextItems.length);
        setItems(nextItems);
        setTotalItems(total);
        setTotalPages(extractTotalPages(response, total));
      }
    } catch (fetchError) {
      setItems([]);
      setTotalItems(0);
      setTotalPages(0);
      setError(fetchError?.response?.data?.message || t("dashboard.esims.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [partnerView, activeStatus, debouncedSearch, startDate, endDate, page, t]);

  useEffect(() => {
    if (partnerView) {
      fetchPartnerStats();
    } else {
      fetchAdminStats();
    }
  }, [partnerView, fetchPartnerStats, fetchAdminStats]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const displayItems = items;

  const partnerStatCards = useMemo(
    () => [
      {
        label: t("dashboard.esims.totalEsims"),
        value: statsLoading ? "—" : extractPartnerStatValue(stats, "total").toLocaleString("en-US"),
        change: formatRate(extractPartnerStatRate(stats, "total")),
        icon: STAT_ICONS.total,
        iconClass: "dashboard-stat-card__icon--blue",
      },
      {
        label: t("dashboard.esims.active"),
        value: statsLoading ? "—" : extractPartnerStatValue(stats, "active").toLocaleString("en-US"),
        change: formatRate(extractPartnerStatRate(stats, "active")),
        icon: STAT_ICONS.active,
        iconClass: "dashboard-stat-card__icon--green",
      },
      {
        label: t("dashboard.esims.expired"),
        value: statsLoading ? "—" : extractPartnerStatValue(stats, "expired").toLocaleString("en-US"),
        change: formatRate(extractPartnerStatRate(stats, "expired")),
        icon: STAT_ICONS.expired,
        iconClass: "dashboard-stat-card__icon--red",
      },
      {
        label: t("dashboard.status.pending"),
        value: statsLoading ? "—" : extractPartnerStatValue(stats, "pending").toLocaleString("en-US"),
        change: formatRate(extractPartnerStatRate(stats, "pending")),
        icon: STAT_ICONS.pending,
        iconClass: "dashboard-stat-card__icon--yellow",
      },
    ],
    [stats, statsLoading, t]
  );

  const adminStatCards = useMemo(
    () => [
      {
        label: t("dashboard.esims.totalEsims"),
        value: statsLoading ? "—" : extractPartnerStatValue(stats, "total").toLocaleString("en-US"),
        change: formatRate(extractPartnerStatRate(stats, "total")) ?? "— no change",
        period: selectedRange.label,
        direction:
          extractPartnerStatRate(stats, "total") == null
            ? "neutral"
            : Number(extractPartnerStatRate(stats, "total")) >= 0
              ? "up"
              : "down",
      },
      {
        label: t("dashboard.esims.active"),
        value: statsLoading ? "—" : extractPartnerStatValue(stats, "active").toLocaleString("en-US"),
        change: formatRate(extractPartnerStatRate(stats, "active")) ?? "— no change",
        period: selectedRange.label,
        direction:
          extractPartnerStatRate(stats, "active") == null
            ? "neutral"
            : Number(extractPartnerStatRate(stats, "active")) >= 0
              ? "up"
              : "down",
      },
      {
        label: t("dashboard.esims.expired"),
        value: statsLoading ? "—" : extractPartnerStatValue(stats, "expired").toLocaleString("en-US"),
        change: formatRate(extractPartnerStatRate(stats, "expired")) ?? "— no change",
        period: selectedRange.label,
        direction:
          extractPartnerStatRate(stats, "expired") == null
            ? "neutral"
            : Number(extractPartnerStatRate(stats, "expired")) >= 0
              ? "up"
              : "down",
      },
      {
        label: t("dashboard.status.pending"),
        value: statsLoading ? "—" : extractPartnerStatValue(stats, "pending").toLocaleString("en-US"),
        change: formatRate(extractPartnerStatRate(stats, "pending")) ?? "— no change",
        period: selectedRange.label,
        direction:
          extractPartnerStatRate(stats, "pending") == null
            ? "neutral"
            : Number(extractPartnerStatRate(stats, "pending")) >= 0
              ? "up"
              : "down",
      },
    ],
    [stats, statsLoading, selectedRange.label, t]
  );

  const paginationPages = getPaginationPages(page, totalPages);
  const rangeStart = totalItems === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, totalItems);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setPage(1);
  };

  const adminColumnCount = 6;
  const partnerColumnCount = 5;

  return (
    <div className="dashboard-page">
      <DashboardPageHeader title={t("dashboard.esims.title")}>
        <label className="dashboard-search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.5" />
            <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <input
            type="search"
            placeholder={t("dashboard.esims.searchPlaceholder")}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label={t("dashboard.esims.searchAria")}
          />
        </label>
        {partnerView ? (
          <>
            <select
              className="dashboard-select"
              value={dateRange}
              onChange={(event) => {
                setDateRange(event.target.value);
                setPage(1);
              }}
              aria-label={t("dashboard.common.dateRange")}
            >
              {dateRanges.map((range) => (
                <option key={range.value} value={range.value}>
                  {range.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="dashboard-btn-outline"
              onClick={() => setIsSyncModalOpen(true)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M4 4v5h5M20 20v-5h-5M20 8a8 8 0 0 0-14.9-3M4 16a8 8 0 0 0 14.9 3"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {t("dashboard.common.sync")}
            </button>
          </>
        ) : (
          <select
            className="dashboard-select"
            value={dateRange}
            onChange={(event) => {
              setDateRange(event.target.value);
              setPage(1);
            }}
            aria-label={t("dashboard.common.dateRange")}
          >
            {dateRanges.map((range) => (
              <option key={range.value} value={range.value}>
                {range.label}
              </option>
            ))}
          </select>
        )}
        <button type="button" className="dashboard-icon-btn" aria-label="Notifications">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13.73 21a2 2 0 0 1-3.46 0"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </DashboardPageHeader>

      <div className="dashboard-stats">
        {partnerView
          ? partnerStatCards.map((stat) => <PartnerStatCard key={stat.label} {...stat} />)
          : adminStatCards.map((stat) => <AdminStatCard key={stat.label} {...stat} />)}
      </div>

      <div className="dashboard-card dashboard-card--table">
        <div className="dashboard-table-toolbar">
          <h2 className="dashboard-card__title dashboard-card__title--inline">{t("dashboard.esims.title")}</h2>
          <div className="dashboard-tabs" role="tablist" aria-label={t("dashboard.esims.paginationAria")}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`dashboard-tabs__item${activeTab === tab.id ? " dashboard-tabs__item--active" : ""}`}
                onClick={() => handleTabChange(tab.id)}
              >
                {tab.id === "all" ? t("dashboard.common.all") : translateEsimStatus(t, tab.status)}
              </button>
            ))}
          </div>
        </div>

        <div className="dashboard-table-wrap">
          <table className="dashboard-table">
            <thead>
              <tr>
                {partnerView ? (
                  <>
                    <th>{t("dashboard.esims.iccid")}</th>
                    <th>{t("dashboard.common.activationCode")}</th>
                    <th>{t("dashboard.common.status")}</th>
                    <th>{t("dashboard.esims.activated")}</th>
                    <th>{t("dashboard.common.created")}</th>
                  </>
                ) : (
                  <>
                    <th>{t("dashboard.common.provider")}</th>
                    <th>{t("dashboard.esims.iccid")}</th>
                    <th>{t("dashboard.common.activationCode")}</th>
                    <th>{t("dashboard.common.status")}</th>
                    <th>{t("dashboard.esims.activated")}</th>
                    <th>{t("dashboard.common.created")}</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={partnerView ? partnerColumnCount : adminColumnCount}>{t("dashboard.esims.loading")}</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={partnerView ? partnerColumnCount : adminColumnCount}>{error}</td>
                </tr>
              ) : displayItems.length === 0 ? (
                <tr>
                  <td colSpan={partnerView ? partnerColumnCount : adminColumnCount}>{t("dashboard.esims.noEsims")}</td>
                </tr>
              ) : partnerView ? (
                displayItems.map((esim) => (
                  <tr key={esim.id}>
                    <td className="dashboard-table__name">{esim.iccid || "—"}</td>
                    <td>
                      <ActivationCodeCell
                        code={esim.activation_code || esim.activationCode}
                        t={t}
                      />
                    </td>
                    <td>
                      <StatusBadge status={esim.status} partnerView t={t} />
                    </td>
                    <td className="dashboard-table__date">
                      {formatDate(esim.activated_at || esim.activatedAt, i18n.language)}
                    </td>
                    <td className="dashboard-table__date">
                      {formatDate(esim.created_at || esim.createdAt, i18n.language)}
                    </td>
                  </tr>
                ))
              ) : (
                displayItems.map((esim) => {
                  const providerName = getProviderLabel(esim);
                  return (
                    <tr key={esim.id}>
                      <td>
                        <div className="dashboard-table__customer">
                          <span
                            className="dashboard-table__avatar"
                            style={{ backgroundColor: avatarColor(providerName) }}
                            aria-hidden="true"
                          >
                            {getInitials(providerName)}
                          </span>
                          <span className="dashboard-table__name">{providerName}</span>
                        </div>
                      </td>
                      <td className="dashboard-table__name">{esim.iccid || "—"}</td>
                      <td>
                        <ActivationCodeCell
                          code={esim.activation_code || esim.activationCode}
                          t={t}
                        />
                      </td>
                      <td>
                        <StatusBadge status={esim.status} partnerView t={t} />
                      </td>
                      <td className="dashboard-table__date">
                        {formatDate(esim.activated_at || esim.activatedAt, i18n.language)}
                      </td>
                      <td className="dashboard-table__date">
                        {formatDate(esim.created_at || esim.createdAt, i18n.language)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="dashboard-table-footer">
          <p className="dashboard-table-footer__info">
            {getShowingText(t, {
              entity: "esims",
              start: rangeStart,
              end: rangeEnd,
              total: totalItems,
            })}
          </p>
          <nav className="dashboard-pagination" aria-label={t("dashboard.esims.paginationAria")}>
            <button
              type="button"
              className="dashboard-pagination__btn"
              aria-label={t("dashboard.common.previousPage")}
              disabled={page <= 1 || loading || totalPages === 0}
              onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M15 18l-6-6 6-6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {paginationPages.map((pageNumber, index) =>
              pageNumber === "ellipsis" ? (
                <span key={`ellipsis-${index}`} className="dashboard-pagination__ellipsis" aria-hidden="true">
                  …
                </span>
              ) : (
                <button
                  key={pageNumber}
                  type="button"
                  className={`dashboard-pagination__page${
                    page === pageNumber ? " dashboard-pagination__page--active" : ""
                  }`}
                  aria-current={page === pageNumber ? "page" : undefined}
                  disabled={loading}
                  onClick={() => setPage(pageNumber)}
                >
                  {pageNumber}
                </button>
              )
            )}
            <button
              type="button"
              className="dashboard-pagination__btn"
              aria-label={t("dashboard.common.nextPage")}
              disabled={page >= totalPages || loading || totalPages === 0}
              onClick={() => setPage((currentPage) => Math.min(totalPages, currentPage + 1))}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M9 18l6-6-6-6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </nav>
        </div>
      </div>

      {partnerView && (
        <SyncConfirmModal
          isOpen={isSyncModalOpen}
          onClose={() => setIsSyncModalOpen(false)}
          onConfirm={() => {
            fetchPartnerStats();
            fetchItems();
          }}
          t={t}
        />
      )}
    </div>
  );
}

export default Esims;
